// ============================================================================
// STUDIO TV — watch page for the fortnightly Co-Creation Studio roundups.
// Player on the left, "Up next" rail on the right (stacked below on narrow
// screens). Autoplay-next with a short countdown, one view per item per
// browser session, and a single refetch when a presigned video URL expires.
// Playlist chips filter Up next and autoplay (?playlist=<id>). Publishers can
// pin, delete, move films between playlists, manage playlists and drag to
// reorder manual ones. CRM impact strip for those allowed to see it.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Tv, Loader, AlertCircle, Pin, PinOff, Trash2, Play, Eye, Lock, Library, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../contexts/AuthContext';
import {
  StudioTvItem,
  StudioTvItemWithVideo,
  StudioTvPlaylist,
  listStudioTv,
  getStudioTvItem,
  recordStudioTvView,
  setStudioTvPinned,
  deleteStudioTvItem,
  markStudioTvSeen,
  audienceLabel,
  moveStudioTvItem,
  reorderStudioTvPlaylist,
  setStudioTvAudience
} from '../services/studio-tv';
import CrmImpactStrip from '../components/studio-tv/CrmImpactStrip';
import SessionStatsStrip from '../components/studio-tv/SessionStatsStrip';
import AudienceDialog from '../components/studio-tv/AudienceDialog';
import PlaylistsDialog from '../components/studio-tv/PlaylistsDialog';
import { ConfirmModal } from '../components/common/ConfirmModal';

const AUTOPLAY_KEY = 'studioTvAutoplay';
const COUNTDOWN_SEC = 5;

function formatDuration(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function formatEdition(edition: string): string {
  const d = new Date(`${edition}T00:00:00`);
  return isNaN(d.getTime()) ? edition : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function sortItems(items: StudioTvItem[]): StudioTvItem[] {
  return [...items].sort((a, b) =>
    a.pinned !== b.pinned ? (a.pinned ? -1 : 1) : b.publishedAt.localeCompare(a.publishedAt));
}

// Films of one playlist in watch order: manual by position, otherwise server order
function orderedFor(items: StudioTvItem[], playlist: StudioTvPlaylist | null): StudioTvItem[] {
  if (!playlist) return items;
  const own = items.filter(i => i.playlistId === playlist.id);
  return playlist.order === 'manual' ? own.sort((a, b) => a.position - b.position) : sortItems(own);
}

interface RowProps {
  item: StudioTvItem;
  canPublish: boolean;
  active?: boolean;
  onOpen: () => void;
  handle?: React.ReactNode;
}

function UpNextRow({ item, canPublish, active, onOpen, handle }: RowProps) {
  return (
    <div className={`flex items-center gap-1 rounded-xl ${active ? 'bg-white shadow-sm ring-1 ring-primary/30' : ''}`}>
      {handle}
      <button onClick={onOpen}
        className="flex flex-1 min-w-0 gap-3 text-left rounded-xl p-1.5 hover:bg-white hover:shadow-sm transition-all">
        <div className="relative w-40 flex-shrink-0 aspect-video rounded-lg overflow-hidden bg-slate-800">
          {item.posterUrl
            ? <img src={item.posterUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
            : <div className="w-full h-full flex items-center justify-center text-white/40"><Tv size={20} /></div>}
          <span className="absolute bottom-1 right-1 bg-black/80 text-white text-[11px] font-semibold rounded px-1 py-0.5 leading-none">
            {formatDuration(item.durationSec)}
          </span>
        </div>
        <div className="min-w-0 flex flex-col gap-1">
          <span className="text-sm font-semibold text-navy line-clamp-2">{item.title}</span>
          <span className="text-xs text-slate-500">{formatEdition(item.edition)}</span>
          {active && <span className="flex items-center gap-1 text-[11px] font-semibold text-primary"><Play size={11} />Now playing</span>}
          {item.pinned && <span className="flex items-center gap-1 text-[11px] font-semibold text-primary"><Pin size={11} />Pinned</span>}
          {canPublish && audienceLabel(item.audience) && (
            <span className="flex items-center gap-1 text-[11px] font-semibold text-amber-700 line-clamp-1"><Lock size={11} />{audienceLabel(item.audience)}</span>
          )}
        </div>
      </button>
    </div>
  );
}

function SortableUpNextRow(props: RowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.item.id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : undefined }}
      className={isDragging ? 'opacity-80' : ''}>
      <UpNextRow {...props} handle={
        <button type="button" aria-label="Drag to reorder" {...attributes} {...listeners}
          className="p-1 text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing touch-none flex-shrink-0">
          <GripVertical size={16} />
        </button>
      } />
    </div>
  );
}

function readAutoplay(): boolean {
  try { return localStorage.getItem(AUTOPLAY_KEY) !== 'false'; } catch { return true; }
}

function claimView(id: string): boolean {
  const key = `studioTvViewed:${id}`;
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
  } catch {}
  return true;
}

export default function StudioTv() {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useAuth();
  const canPublish = can('StudioTv::Publish');
  const canSeeImpact = can('Studio::ViewCrmImpact');

  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<StudioTvItem[] | null>(null);
  const [playlists, setPlaylists] = useState<StudioTvPlaylist[]>([]);
  const [managePlaylists, setManagePlaylists] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [current, setCurrent] = useState<StudioTvItemWithVideo | null>(null);
  const [itemError, setItemError] = useState<string | null>(null);
  const [autoplay, setAutoplay] = useState(readAutoplay);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editAudience, setEditAudience] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const errorRefreshUsed = useRef(false);
  const resumeAt = useRef<number | null>(null);
  const autoplayRequested = !!(location.state as { autoplay?: boolean } | null)?.autoplay;

  useEffect(() => {
    listStudioTv()
      .then(({ items: list, playlists: lists }) => { setItems(list); setPlaylists(lists); markStudioTvSeen(list); })
      .catch(() => setListError('Could not load Studio TV'));
  }, []);

  const playlistParam = searchParams.get('playlist');
  const selectedPlaylist = useMemo(
    () => playlists.find(p => p.id === playlistParam) ?? null, [playlists, playlistParam]);
  const showStats = !selectedPlaylist || selectedPlaylist.isDefault;
  const showChips = playlists.length > 1 || canPublish;
  const canReorder = canPublish && selectedPlaylist?.order === 'manual';

  const ordered = useMemo(() => orderedFor(items || [], selectedPlaylist), [items, selectedPlaylist]);

  // Landing with a playlist chosen starts from that playlist's first film
  const currentId = routeId || ordered[0]?.id || null;

  useEffect(() => {
    if (!currentId) return;
    let cancelled = false;
    setCurrent(null);
    setItemError(null);
    setCountdown(null);
    setActionError(null);
    errorRefreshUsed.current = false;
    resumeAt.current = null;
    getStudioTvItem(currentId)
      .then(item => { if (!cancelled) setCurrent(item); })
      .catch(() => { if (!cancelled) setItemError('This film could not be loaded. It may have been removed.'); });
    return () => { cancelled = true; };
  }, [currentId]);

  // Not in the selected playlist: the playlist starts from its first film
  const nextItem = useMemo(() => {
    if (!currentId) return null;
    const idx = ordered.findIndex(i => i.id === currentId);
    return idx < ordered.length - 1 ? ordered[idx + 1] : null;
  }, [ordered, currentId]);

  const upNext = useMemo(() => ordered.filter(i => i.id !== currentId), [ordered, currentId]);

  const filmPath = (id: string) => `/studio-tv/${id}${location.search}`;

  const selectPlaylist = (id: string | null) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (id) next.set('playlist', id);
      else next.delete('playlist');
      return next;
    }, { replace: true });
  };

  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      setCountdown(null);
      if (nextItem) navigate(filmPath(nextItem.id), { state: { autoplay: true } });
      return;
    }
    const t = setTimeout(() => setCountdown(c => (c === null ? null : c - 1)), 1000);
    return () => clearTimeout(t);
  }, [countdown, nextItem, navigate, location.search]);

  const toggleAutoplay = () => {
    setAutoplay(prev => {
      const next = !prev;
      try { localStorage.setItem(AUTOPLAY_KEY, String(next)); } catch {}
      if (!next) setCountdown(null);
      return next;
    });
  };

  const patchItem = useCallback((id: string, patch: Partial<StudioTvItem>) => {
    setItems(prev => prev ? sortItems(prev.map(i => (i.id === id ? { ...i, ...patch } : i))) : prev);
    setCurrent(prev => (prev && prev.id === id ? { ...prev, ...patch } : prev));
  }, []);

  const adjustCount = (playlistId: string, delta: number) =>
    setPlaylists(prev => prev.map(p => (p.id === playlistId && p.videoCount !== undefined
      ? { ...p, videoCount: Math.max(0, p.videoCount + delta) } : p)));

  const handleMove = async (playlistId: string) => {
    if (!current || playlistId === current.playlistId) return;
    const from = current.playlistId;
    setBusy(true);
    setActionError(null);
    try {
      const updated = await moveStudioTvItem(current.id, playlistId);
      patchItem(updated.id, { playlistId: updated.playlistId, position: updated.position });
      adjustCount(from, -1);
      adjustCount(updated.playlistId, 1);
    } catch (err: any) {
      setActionError(err?.response?.data?.error || 'Could not move this film');
    } finally {
      setBusy(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!selectedPlaylist || !over || active.id === over.id) return;
    const from = ordered.findIndex(i => i.id === active.id);
    const to = ordered.findIndex(i => i.id === over.id);
    if (from < 0 || to < 0) return;
    const ids = arrayMove(ordered, from, to).map(i => i.id);
    const before = items;
    const positions = new Map(ids.map((id, idx) => [id, idx]));
    setItems(prev => prev ? prev.map(i => (positions.has(i.id) ? { ...i, position: positions.get(i.id)! } : i)) : prev);
    try {
      await reorderStudioTvPlaylist(selectedPlaylist.id, ids);
    } catch (err: any) {
      setItems(before);
      toast.error(err?.response?.data?.error || 'Could not save the new order');
    }
  };

  const handlePlay = () => {
    setCountdown(null);
    if (!current || !claimView(current.id)) return;
    const id = current.id;
    recordStudioTvView(id).then(views => patchItem(id, { views })).catch(() => {});
  };

  const handleEnded = () => {
    if (autoplay && nextItem) setCountdown(COUNTDOWN_SEC);
  };

  const handleError = () => {
    if (!current || errorRefreshUsed.current) return;
    errorRefreshUsed.current = true;
    resumeAt.current = videoRef.current?.currentTime || 0;
    const id = current.id;
    getStudioTvItem(id)
      .then(fresh => setCurrent(prev => (prev && prev.id === id ? { ...fresh, views: prev.views } : prev)))
      .catch(() => setItemError('The video could not be played. Try reloading the page.'));
  };

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video || resumeAt.current === null) return;
    video.currentTime = resumeAt.current;
    resumeAt.current = null;
    // The refreshed URL works, so a later expiry may refresh again
    errorRefreshUsed.current = false;
    video.play().catch(() => {});
  };

  const togglePin = async () => {
    if (!current) return;
    setBusy(true);
    setActionError(null);
    try {
      const updated = await setStudioTvPinned(current.id, !current.pinned);
      patchItem(updated.id, { pinned: updated.pinned });
    } catch {
      setActionError('Could not update the pin');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!current) return;
    setBusy(true);
    setActionError(null);
    try {
      await deleteStudioTvItem(current.id);
      const id = current.id;
      setItems(prev => prev ? prev.filter(i => i.id !== id) : prev);
      adjustCount(current.playlistId, -1);
      setConfirmDelete(false);
      navigate(`/studio-tv${location.search}`);
    } catch {
      setActionError('Could not delete this film');
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  };

  const secondaryBtn = 'flex items-center justify-center gap-2 rounded-xl h-9 px-3 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-50';

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Tv size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Studio TV</h1>
              <p className="text-slate-500 text-sm font-medium">Fortnightly roundups from the Co-Creation Studio</p>
            </div>
          </div>
          {canPublish && (
            <button onClick={() => setManagePlaylists(true)} className={secondaryBtn}>
              <Library size={16} /> Playlists
            </button>
          )}
        </header>

        {/* One row of figures on wide screens; the two groups stack below xl */}
        {showStats && (
          <div className="flex flex-col xl:flex-row gap-6">
            <SessionStatsStrip />
            {canSeeImpact && <CrmImpactStrip />}
          </div>
        )}

        {showChips && items !== null && (
          <div className="flex flex-wrap items-center gap-2">
            {[{ id: null, title: 'All' } as const, ...playlists].map(p => {
              const on = (p.id ?? null) === (selectedPlaylist?.id ?? null);
              const lock = canPublish && p.id ? audienceLabel(playlists.find(x => x.id === p.id)?.audience) : null;
              return (
                <button key={p.id ?? 'all'} onClick={() => selectPlaylist(p.id)} aria-pressed={on}
                  className={`flex items-center gap-1.5 rounded-full h-8 px-4 text-sm font-bold border transition-colors ${
                    on ? 'bg-primary border-primary text-white shadow-sm' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                  {p.title}
                  {lock && (
                    <span className={`flex items-center gap-1 text-[11px] font-semibold ${on ? 'text-white/80' : 'text-amber-700'}`}
                      title="Only these roles (and publishers) can see this playlist">
                      <Lock size={11} />{lock}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {listError ? (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{listError}</p>
          </div>
        ) : items === null ? (
          <div className="flex justify-center items-center py-20 text-[#64748b]">
            <Loader className="w-6 h-6 animate-spin" />
          </div>
        ) : items.length === 0 && !routeId ? (
          <div className="rounded-xl bg-white p-12 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Tv size={28} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">No roundups published yet.</p>
            <p className="text-slate-400 text-xs mt-2">New editions appear here every fortnight.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,7fr)_minmax(0,3fr)] gap-6">

            {/* Player */}
            <div className="flex flex-col gap-4 min-w-0">
              <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden">
                {current ? (
                  <video
                    key={current.id}
                    ref={videoRef}
                    className="w-full h-full"
                    controls
                    preload="metadata"
                    playsInline
                    autoPlay={autoplayRequested}
                    poster={current.posterUrl || undefined}
                    src={current.videoUrl}
                    onPlay={handlePlay}
                    onEnded={handleEnded}
                    onError={handleError}
                    onLoadedMetadata={handleLoadedMetadata}
                  />
                ) : !itemError && (
                  <div className="absolute inset-0 flex items-center justify-center text-white/60">
                    <Loader className="w-6 h-6 animate-spin" />
                  </div>
                )}
                {itemError && (
                  <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-white/80 px-6 text-center">
                    <AlertCircle size={16} /> {itemError}
                  </div>
                )}
                {countdown !== null && nextItem && (
                  <div className="absolute inset-0 bg-black/75 flex flex-col items-center justify-center gap-3 text-white px-6 text-center">
                    <span className="text-xs uppercase tracking-widest text-white/60">Up next in {countdown}</span>
                    <span className="text-lg font-bold">{nextItem.title}</span>
                    <div className="flex gap-3 mt-1">
                      <button onClick={() => setCountdown(null)}
                        className="rounded-xl h-9 px-4 border border-white/30 text-sm font-bold hover:bg-white/10 transition-colors">
                        Cancel
                      </button>
                      <button onClick={() => setCountdown(0)}
                        className="flex items-center gap-2 rounded-xl h-9 px-4 bg-primary hover:bg-primary-dark text-sm font-bold transition-colors">
                        <Play size={14} /> Play now
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {current && (
                <div className="flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-xl font-bold text-navy">{current.title}</h2>
                      <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                        <span>{formatEdition(current.edition)}</span>
                        <span>·</span>
                        <span className="flex items-center gap-1"><Eye size={14} />{current.views} {current.views === 1 ? 'view' : 'views'}</span>
                        {current.pinned && <><span>·</span><span className="flex items-center gap-1 text-primary"><Pin size={13} />Pinned</span></>}
                        {canPublish && audienceLabel(current.audience) && (
                          <><span>·</span><span className="flex items-center gap-1 text-amber-700" title="Only these roles (and publishers) can see this film">
                            <Lock size={13} />{audienceLabel(current.audience)}
                          </span></>
                        )}
                      </p>
                    </div>
                    {canPublish && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {playlists.length > 0 && (
                          <select value={current.playlistId} onChange={e => handleMove(e.target.value)} disabled={busy}
                            aria-label="Move to playlist"
                            className="rounded-xl h-9 px-3 border border-slate-200 bg-white text-slate-600 text-sm font-bold shadow-sm disabled:opacity-50 max-w-[180px]">
                            {!playlists.some(p => p.id === current.playlistId) && <option value={current.playlistId} disabled>Move to playlist</option>}
                            {playlists.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                          </select>
                        )}
                        <button onClick={() => setEditAudience(true)} disabled={busy} className={secondaryBtn}>
                          <Lock size={16} /> Audience
                        </button>
                        <button onClick={togglePin} disabled={busy} className={secondaryBtn}>
                          {current.pinned ? <><PinOff size={16} /> Unpin</> : <><Pin size={16} /> Pin</>}
                        </button>
                        <button onClick={() => setConfirmDelete(true)} disabled={busy}
                          className={`${secondaryBtn} hover:text-red-600 hover:border-red-200`}>
                          <Trash2 size={16} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                  {actionError && <p className="text-xs text-red-600">{actionError}</p>}
                  {current.description && (
                    <div className="rounded-xl bg-white border border-[#e2e8f0] p-4 text-sm text-slate-600 whitespace-pre-line">
                      {current.description}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Up next */}
            <aside className="flex flex-col gap-3 min-w-0">
              <div className="flex items-center justify-between">
                <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">Up next</h2>
                <label className="flex items-center gap-2 text-xs font-medium text-slate-500 cursor-pointer select-none">
                  Autoplay
                  <button type="button" role="switch" aria-checked={autoplay} onClick={toggleAutoplay}
                    className={`relative w-8 h-4 rounded-full transition-colors ${autoplay ? 'bg-primary' : 'bg-slate-300'}`}>
                    <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${autoplay ? 'left-[18px]' : 'left-0.5'}`} />
                  </button>
                </label>
              </div>
              {canReorder ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
                  <SortableContext items={ordered.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    {ordered.map(item => (
                      <SortableUpNextRow key={item.id} item={item} canPublish={canPublish}
                        active={item.id === currentId} onOpen={() => navigate(filmPath(item.id))} />
                    ))}
                  </SortableContext>
                </DndContext>
              ) : upNext.length === 0 ? (
                <p className="text-xs text-slate-400">{selectedPlaylist ? 'No other films in this playlist.' : 'No other editions yet.'}</p>
              ) : upNext.map(item => (
                <UpNextRow key={item.id} item={item} canPublish={canPublish} onOpen={() => navigate(filmPath(item.id))} />
              ))}
            </aside>
          </div>
        )}
      </div>

      {editAudience && current && (
        <AudienceDialog initial={current.audience} onClose={() => setEditAudience(false)}
          onSave={async ids => {
            const updated = await setStudioTvAudience(current.id, ids);
            patchItem(updated.id, { audience: updated.audience });
            setEditAudience(false);
          }} />
      )}

      {managePlaylists && (
        <PlaylistsDialog
          playlists={playlists}
          onClose={() => setManagePlaylists(false)}
          onCreated={p => setPlaylists(prev => [...prev, p])}
          onUpdated={p => setPlaylists(prev => prev.map(x => (x.id === p.id ? { ...x, ...p } : x)))}
          onDeleted={id => {
            setPlaylists(prev => prev.filter(x => x.id !== id));
            if (playlistParam === id) selectPlaylist(null);
          }} />
      )}

      <ConfirmModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Delete this film?"
        message={`"${current?.title || ''}" will be removed from Studio TV for everyone, along with its video and poster. This cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isProcessing={busy}
      />
    </div>
  );
}
